<template>
    <!--
      SSH 文件面板（内嵌在控制台抽屉右侧，不是独立抽屉）
      入口在控制台的「文件」按钮：开完控制台后想传/下个文件，就地展开即可，终端始终可见。
      宽度可拖拽（左边缘），宽度值记在 localStorage 里，刷新后保持。
    -->
    <div class="fp" :class="{ 'is-resizing': resizing }" :style="{ '--fp-width': panelWidth + 'px' }">
        <!-- 拖拽手柄：压在面板左边缘上，命中区域比视觉宽度大一些 -->
        <div class="fp-resizer" :class="{ 'is-active': resizing }" title="拖动调整宽度"
            @mousedown.prevent="startResize" @touchstart.prevent="startResize"></div>
        <!-- 面板头 -->
        <div class="fp-head">
            <span class="fp-head__title">文件</span>
            <span class="fp-head__count">{{ items.length }} 项</span>
            <div class="fp-head__ops">
                <el-tooltip content="刷新" placement="top">
                    <el-button class="fp-icon-btn" size="small" text :disabled="loading || uploading"
                        @click="refresh">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                    </el-button>
                </el-tooltip>
                <el-tooltip content="上传到当前目录" placement="top">
                    <el-button class="fp-icon-btn" size="small" text type="primary" :loading="uploading"
                        :disabled="loading" @click="pickFile">
                        <el-icon v-if="!uploading">
                            <Upload />
                        </el-icon>
                    </el-button>
                </el-tooltip>
                <el-tooltip content="收起文件面板" placement="top">
                    <el-button class="fp-icon-btn" size="small" text @click="$emit('close')">
                        <el-icon>
                            <Close />
                        </el-icon>
                    </el-button>
                </el-tooltip>
            </div>
        </div>

        <!-- 路径栏 -->
        <div class="fp-path">
            <el-tooltip content="上级目录" placement="top">
                <el-button class="fp-path__btn" size="small" text :disabled="!canGoUp || loading" @click="goUp">
                    <el-icon>
                        <ArrowLeft />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <!-- 面包屑：点任意一层可直接跳过去，不用逐层点「上级」 -->
            <div class="fp-crumb" :title="path">
                <span class="fp-crumb__item" :class="{ 'is-current': !breadcrumbs.length }" @click="goTo('/')">
                    /
                </span>
                <template v-for="(item, index) in breadcrumbs" :key="item.path">
                    <span v-if="index > 0" class="fp-crumb__sep">/</span>
                    <span class="fp-crumb__item" :class="{ 'is-current': item.current }"
                        @click="goTo(item.path)">{{ item.name }}</span>
                </template>
            </div>
            <el-tooltip content="复制当前路径" placement="top">
                <el-button class="fp-path__btn" size="small" text :disabled="!path" @click="copyPath">
                    <el-icon>
                        <DocumentCopy />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <el-tooltip :content="showHidden ? '不显示隐藏文件' : '显示隐藏文件'" placement="top">
                <el-button class="fp-path__btn" :class="{ 'is-on': showHidden }" size="small" text
                    @click="showHidden = !showHidden">
                    <el-icon>
                        <View />
                    </el-icon>
                </el-button>
            </el-tooltip>
        </div>

        <!-- 文件列表：窄面板用两行式列表，比表格更省横向空间 -->
        <div v-loading="loading" class="fp-list">
            <div v-for="item in visibleItems" :key="item.path" class="fp-row" @dblclick="onRowDblclick(item)">
                <el-icon class="fp-row__icon" :class="{ 'is-dir': item.dir }">
                    <Folder v-if="item.dir" />
                    <Link v-else-if="item.link" />
                    <Document v-else />
                </el-icon>
                <div class="fp-row__main">
                    <div class="fp-row__name" :class="{ 'is-dir': item.dir }" :title="item.name"
                        @click="onNameClick(item)">
                        {{ item.name }}
                    </div>
                    <div class="fp-row__meta">
                        <span :title="item.dir ? '目录' : item.sizeText">{{ item.dir ? '-' : item.sizeText }}</span>
                        <span class="fp-row__sep">·</span>
                        <span>{{ item.modifyTime }}</span>
                        <span class="fp-row__perm" :title="item.permission">{{ item.permission }}</span>
                    </div>
                </div>
                <div v-if="!item.dir" class="fp-row__ops">
                    <el-tooltip v-if="canPreview(item)" content="在线预览" placement="top">
                        <el-button class="fp-icon-btn" size="small" text type="primary"
                            @click.stop="openPreview(item)">
                            <el-icon>
                                <View />
                            </el-icon>
                        </el-button>
                    </el-tooltip>
                    <el-tooltip content="下载" placement="top">
                        <el-button class="fp-icon-btn" size="small" text type="primary" :disabled="downloading"
                            @click.stop="download(item)">
                            <el-icon>
                                <Download />
                            </el-icon>
                        </el-button>
                    </el-tooltip>
                </div>
            </div>
            <div v-if="!loading && visibleItems.length === 0" class="fp-empty">
                {{ items.length === 0 ? '这个目录是空的' : '隐藏了 ' + (items.length - visibleItems.length) + ' 个隐藏文件' }}
            </div>
        </div>

        <!-- 隐藏的上传输入框（用原生 input，避免额外引入组件） -->
        <input ref="fileInput" class="fp-input" type="file" @change="onFilePicked" />
    </div>

    <!-- 文本预览：不方便下载的场景可以直接看内容 -->
    <el-dialog v-model="preview.visible" class="ssh-file-preview" width="80%" top="6vh" append-to-body
        :title="preview.name || '文件预览'">
        <div class="ssh-file-preview__bar">
            <span class="ssh-file-preview__path" :title="preview.path">{{ preview.path }}</span>
            <span class="ssh-file-preview__size">{{ preview.sizeText }}</span>
            <!-- 自动换行：行数不多时行号与换行可以兼得，行数多时只能二选一（见 previewWrapTitle） -->
            <el-checkbox v-model="previewWrap" class="ssh-file-preview__wrap" :title="previewWrapTitle">
                自动换行
            </el-checkbox>
            <el-button class="ssh-file-preview__btn" size="small" type="primary" plain
                :disabled="preview.loading || !preview.rawContent" @click="toggleJsonFormat">
                {{ preview.formatted ? '还原原文' : '格式化 JSON' }}
            </el-button>
        </div>
        <!-- 文件属性：修改时间 + 权限（含人类语言说明），一眼看懂"这个文件是什么、谁能动它" -->
        <div class="ssh-file-preview__meta">
            <span class="ssh-file-preview__meta-item">最后修改：{{ preview.modifyTime || '-' }}</span>
            <span class="ssh-file-preview__meta-sep">·</span>
            <span class="ssh-file-preview__meta-item">
                权限：<span class="ssh-file-preview__perm">{{ preview.permission || '-' }}</span>
                <span v-if="permissionOctal" class="ssh-file-preview__octal">（{{ permissionOctal }}）</span>
            </span>
            <template v-if="permissionDescription">
                <span class="ssh-file-preview__meta-sep">·</span>
                <span class="ssh-file-preview__meta-item is-desc">{{ permissionDescription }}</span>
            </template>
        </div>
        <div v-loading="preview.loading" class="ssh-file-preview__body">
            <!--
              逐行渲染：换行后的续行属于同一逻辑行，行号只在该行顶部出现一次（与编辑器一致）。
              行数超过阈值时改走下面的整块方案，避免 DOM 爆炸。
            -->
            <div v-if="previewWrap && canWrapWithLineNumbers" class="ssh-file-preview__rows">
                <div v-for="(line, index) in previewLines" :key="index" class="ssh-file-preview__row">
                    <span class="ssh-file-preview__row-no">{{ index + 1 }}</span>
                    <span class="ssh-file-preview__row-text">{{ line }}</span>
                </div>
            </div>
            <div v-else class="ssh-file-preview__inner" :class="{ 'is-wrap': previewWrap }">
                <pre v-if="!previewWrap" class="ssh-file-preview__gutter">{{ previewLineNumbers }}</pre>
                <pre class="ssh-file-preview__code">{{ previewText }}</pre>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request.js';
import { config } from '@/utils/config';

// 面板宽度（拖拽后记住，刷新页面仍保持）
const PANEL_WIDTH_KEY = 'hidoc_ssh_file_panel_width';
const PANEL_WIDTH_DEFAULT = 380;
const PANEL_WIDTH_MIN = 260;
const PANEL_WIDTH_MAX = 720;
// 预览是否自动换行的偏好
const PREVIEW_WRAP_KEY = 'hidoc_ssh_preview_wrap';
/**
 * 预览逐行渲染的行数上限。
 * 只有逐行渲染才能让「自动换行」与「保留逻辑行号」兼得（换行后的续行共用同一个行号、
 * 不重复显示），但 DOM 节点数等于行数，所以超过这个量就退回"整块 <pre>"方案 ——
 * 那种方案下换行会导致行号无法对齐，只能隐藏行号。
 * 3000 行 ≈ 9k 个节点，打开无感知；2MB 日志可达数万行，绝不能逐行渲染。
 */
const WRAP_LINE_LIMIT = 3000;

/**
 * localStorage 在隐私模式或被禁用时会直接抛错，
 * 所以统一兜一层：读写失败就当作没有偏好，绝不影响主功能。
 */
function readLocal(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function writeLocal(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // 存不了就算了
    }
}

export default {
    name: 'SshFilePanel',
    props: {
        machine: { type: Object, default: () => ({}) }
    },
    emits: ['close'],
    data() {
        return {
            // 当前目录（由后端返回的规范化绝对路径）
            path: '',
            items: [],
            loading: false,
            // 同一时刻只允许一个下载/上传，避免误触
            downloading: false,
            uploading: false,
            showHidden: false,
            // 面板宽度与拖拽状态（宽度经 CSS 变量下发，见模板 style）
            panelWidth: PANEL_WIDTH_DEFAULT,
            resizing: false,
            resizeStartX: 0,
            resizeStartWidth: 0,
            // 拖拽前的 body user-select，结束后还原
            prevBodyUserSelect: '',
            // 预览是否自动换行（开启则隐藏行号列）
            previewWrap: false,
            // 预览大小上限（由后端 list 接口下发，取值见 SshSftpService.PREVIEW_MAX_BYTES）
            previewMaxBytes: 0,
            preview: {
                visible: false,
                loading: false,
                name: '',
                path: '',
                sizeText: '',
                // 修改时间与权限来自列表行（预览接口只返回内容，不必为此再查一次文件属性）
                modifyTime: '',
                permission: '',
                content: '',
                // 原文永久保留：JSON 格式化以它为源，反复切换不会层层缩进
                rawContent: '',
                formatted: false
            }
        };
    },
    computed: {
        visibleItems() {
            // 后端会返回点开头的隐藏文件，这里做一个显示开关
            return this.showHidden ? this.items : this.items.filter(item => !item.name.startsWith('.'));
        },
        canGoUp() {
            return !!this.path && this.path !== '/';
        },
        /** 权限的八进制写法（如 644 / 4755），方便直接对着敲 chmod */
        permissionOctal() {
            const perm = this.preview.permission;
            if (!perm || perm.length < 10) {
                return '';
            }
            const toDigit = bits => (bits[0] === 'r' ? 4 : 0) + (bits[1] === 'w' ? 2 : 0)
                + ('xst'.includes(bits[2]) ? 1 : 0);
            const digits = String(toDigit(perm.slice(1, 4))) + String(toDigit(perm.slice(4, 7)))
                + String(toDigit(perm.slice(7, 10)));
            // setuid / setgid / sticky 是第四位（只在存在时才显示，避免把 4755 说成 755）
            const special = (perm[3] === 's' || perm[3] === 'S' ? 4 : 0)
                + (perm[6] === 's' || perm[6] === 'S' ? 2 : 0)
                + (perm[9] === 't' || perm[9] === 'T' ? 1 : 0);
            return special > 0 ? String(special) + digits : digits;
        },
        /** 把 -rw-r--r-- 这类权限串翻译成人话 */
        permissionDescription() {
            const perm = this.preview.permission;
            if (!perm || perm.length < 10) {
                return '';
            }
            const typeMap = {
                '-': '普通文件', d: '目录', l: '软链接',
                c: '字符设备', b: '块设备', p: '命名管道', s: '套接字'
            };
            const isDir = perm[0] === 'd';
            // 目录与普通文件的 r/w/x 语义不同，分开说才准确
            const readText = isDir ? '列目录' : '读';
            const writeText = isDir ? '增删' : '写';
            const execText = isDir ? '进入' : '执行';
            const describe = bits => {
                const parts = [];
                if (bits[0] === 'r') {
                    parts.push(readText);
                }
                if (bits[1] === 'w') {
                    parts.push(writeText);
                }
                // 执行位可能是 s / S / t / T（带特殊位时的大小写形式）
                if ('xst'.includes(bits[2])) {
                    parts.push(execText);
                }
                return parts.length ? parts.join('、') : '无权限';
            };
            return (typeMap[perm[0]] || '未知类型')
                + ' ｜ 属主：' + describe(perm.slice(1, 4))
                + ' ｜ 同组：' + describe(perm.slice(4, 7))
                + ' ｜ 其他人：' + describe(perm.slice(7, 10));
        },
        /**
         * 路径面包屑：把 /home/user/www 拆成 [{home,/home}, {user,/home/user}, {www,/home/user/www}]
         * 根目录 `/` 单独渲染（它就是开头的那个斜杠），所以这里不包含根
         */
        breadcrumbs() {
            if (!this.path || this.path === '/') {
                return [];
            }
            const names = this.path.replace(/^\//, '').split('/');
            return names.map((name, index) => ({
                name,
                path: '/' + names.slice(0, index + 1).join('/'),
                current: index === names.length - 1
            }));
        },
        /**
         * 去掉结尾的一个换行：
         * `pre` 对"结尾空行"的渲染在各浏览器上不一致，而这里行号与内容必须严格 1:1，
         * 所以两者都基于这个字符串计算，避免底部多出一个行号
         */
        previewText() {
            const content = this.preview.content || '';
            return content.endsWith('\n') ? content.slice(0, -1) : content;
        },
        /** 预览内容的逻辑行（只有逐行渲染模式会访问，行数已限制在阈值内） */
        previewLines() {
            return this.previewText ? this.previewText.split('\n') : [];
        },
        /**
         * 逻辑行数。**故意不用 `previewLines.length`**：那样会为了拿一个数字，
         * 把整份内容（最大 2MB、可达数万行）切成数组常驻内存。
         * 这里只扫一遍换行符，大文件下不产生任何数组。
         */
        previewLineCount() {
            const text = this.previewText;
            if (!text) {
                return 0;
            }
            let count = 1;
            for (let i = 0; i < text.length; i++) {
                if (text.charCodeAt(i) === 10) {
                    count += 1;
                }
            }
            return count;
        },
        /**
         * 行数在阈值内才逐行渲染 —— 只有那样才能让「自动换行」与「保留逻辑行号」兼得。
         * 超过阈值退回整块 <pre>，此时换行会导致行号无法对齐，只能隐藏行号。
         */
        canWrapWithLineNumbers() {
            return this.previewLineCount <= WRAP_LINE_LIMIT;
        },
        /** 复选框提示：行为随行数不同，提示也要跟着变，别让用户以为行号"莫名消失" */
        previewWrapTitle() {
            return this.canWrapWithLineNumbers
                ? '开启后内容自动换行；行号保留，同一逻辑行换行出的续行不重复显示行号'
                : `开启后内容自动换行；当前 ${this.previewLineCount} 行较多，为保证流畅会隐藏行号`;
        },
        /**
         * 行号列的内容（整块 <pre> 方案用）。
         * 【为什么是整块文本而不是逐行 v-for】整块方案的性能优势就在这里：整列只是一个文本节点，
         * 靠相同的 font-size / line-height 与内容严格对齐，行数再多也不产生额外 DOM。
         */
        previewLineNumbers() {
            const count = this.previewLineCount;
            if (!count) {
                return '';
            }
            const lines = new Array(count);
            for (let i = 0; i < count; i++) {
                lines[i] = i + 1;
            }
            return lines.join('\n');
        }
    },
    watch: {
        // 记住"自动换行"的偏好，下次预览沿用
        previewWrap(value) {
            writeLocal(PREVIEW_WRAP_KEY, value ? '1' : '0');
        }
    },
    mounted() {
        // 恢复上次的面板宽度与预览换行偏好
        const savedWidth = Number(readLocal(PANEL_WIDTH_KEY));
        if (!Number.isNaN(savedWidth) && savedWidth > 0) {
            this.panelWidth = this.clampPanelWidth(savedWidth);
        }
        this.previewWrap = readLocal(PREVIEW_WRAP_KEY) === '1';

        // 面板是「展开时才创建」的，所以直接在 mounted 里加载，不需要 watch 显隐
        this.load('');
    },
    beforeUnmount() {
        // 极端情况：拖拽过程中面板被卸载（关抽屉），把全局副作用收干净
        this.stopResize();
    },
    methods: {
        // ---------- 面板宽度拖拽 ----------
        /** 把宽度夹在合理区间：既不能窄到没法用，也要给左侧终端留出空间 */
        clampPanelWidth(width) {
            // 视口太窄时（比如分屏）保证终端至少还有 320px
            const viewportMax = Math.max(PANEL_WIDTH_MIN, window.innerWidth - 320);
            return Math.min(PANEL_WIDTH_MAX, viewportMax, Math.max(PANEL_WIDTH_MIN, width));
        },
        /** 面板在右侧：鼠标往左拖 → delta 为正 → 面板变宽 */
        startResize(event) {
            if (event.touches && event.touches.length > 1) {
                return;
            }
            this.resizing = true;
            this.resizeStartX = event.touches ? event.touches[0].clientX : event.clientX;
            this.resizeStartWidth = this.panelWidth;
            // 拖拽期间禁止选中文本 / 固定光标，否则会一边拖一边把页面文字选中
            this.prevBodyUserSelect = document.body.style.userSelect;
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
            document.addEventListener('mousemove', this.onResizeMove);
            document.addEventListener('mouseup', this.stopResize);
            document.addEventListener('touchmove', this.onResizeMove, { passive: false });
            document.addEventListener('touchend', this.stopResize);
        },
        onResizeMove(event) {
            if (!this.resizing) {
                return;
            }
            // 阻止 touchmove 的默认行为，避免拖拽时页面跟着滚
            if (event.cancelable) {
                event.preventDefault();
            }
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            this.panelWidth = this.clampPanelWidth(this.resizeStartWidth + (this.resizeStartX - clientX));
            // 终端的新尺寸由控制台里的 ResizeObserver 接管（那里已有 80ms 防抖），
            // 所以这里不需要额外节流，也不会狂发 resize 帧
        },
        stopResize() {
            if (!this.resizing) {
                return;
            }
            this.resizing = false;
            document.body.style.userSelect = this.prevBodyUserSelect;
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', this.onResizeMove);
            document.removeEventListener('mouseup', this.stopResize);
            document.removeEventListener('touchmove', this.onResizeMove);
            document.removeEventListener('touchend', this.stopResize);
            // 拖完才落盘，避免拖拽过程中反复写 localStorage
            writeLocal(PANEL_WIDTH_KEY, String(this.panelWidth));
        },

        // ---------- 浏览 ----------
        refresh() {
            this.load(this.path);
        },
        goUp() {
            if (!this.canGoUp) {
                return;
            }
            const index = this.path.lastIndexOf('/');
            this.load(index <= 0 ? '/' : this.path.substring(0, index));
        },
        /** 点面包屑的某一层：直接跳到那一层（点当前层不重复请求） */
        goTo(path) {
            if (!path || path === this.path || this.loading) {
                return;
            }
            this.load(path);
        },
        load(path) {
            if (!this.machine || !this.machine.id) {
                return;
            }
            this.loading = true;
            // path 传空字符串时后端会打开登录用户的 home 目录
            request({
                url: '/serverManFile/list',
                method: 'post',
                data: { machineId: this.machine.id, path: path || '' }
            })
                .then(res => {
                    this.path = res.meta.path;
                    this.items = res.meta.items || [];
                    this.previewMaxBytes = res.meta.previewMaxBytes || 0;
                })
                .catch(() => {
                    // 错误提示由 request 拦截器统一处理
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onNameClick(item) {
            if (item.dir) {
                this.load(item.path);
            }
        },
        onRowDblclick(item) {
            if (item.dir) {
                this.load(item.path);
            }
        },

        /** 复制当前完整路径 */
        copyPath() {
            const text = this.path;
            if (!text) {
                return;
            }
            // 优先用 Clipboard API：它只在**安全上下文**（https 或 localhost）下可用
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text)
                    .then(() => ElMessage.success('已复制路径'))
                    .catch(() => this.copyPathFallback(text));
                return;
            }
            this.copyPathFallback(text);
        },
        /**
         * 兜底复制：用临时 textarea + execCommand。
         * 场景：通过 http + 非 localhost 访问时（比如 http://192.168.1.5:8001），
         * Clipboard API 整个不可用，只能用这个已废弃但仍被浏览器支持的老办法。
         */
        copyPathFallback(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            // 固定在视口外并透明，避免复制时页面跳动
            textarea.style.position = 'fixed';
            textarea.style.top = '-1000px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            let ok = false;
            try {
                ok = document.execCommand('copy');
            } catch (e) {
                ok = false;
            }
            document.body.removeChild(textarea);
            if (ok) {
                ElMessage.success('已复制路径');
            } else {
                ElMessage({ message: '复制失败，请手动选中路径复制', type: 'warning', duration: 3 * 1000 });
            }
        },

        // ---------- 下载 ----------
        download(item) {
            if (this.downloading) {
                return;
            }
            this.downloading = true;
            // 浏览器原生下载无法自定义请求头，所以先用正常鉴权换一张一次性票
            request({
                url: '/serverManFile/ticket',
                method: 'post',
                data: { machineId: this.machine.id, path: item.path }
            })
                .then(res => {
                    this.openDownloadUrl(res.meta.ticket);
                })
                .catch(() => {})
                .finally(() => {
                    this.downloading = false;
                });
        },
        openDownloadUrl(ticket) {
            // 后端返回的是 Content-Disposition: attachment，浏览器会在新标签里开始下载并自动关掉它；
            // 用新标签而不是当前页跳转，是为了让票失效等错误不影响当前页面
            const baseServer = (config() && config().baseServer) || '';
            const url = baseServer.replace(/\/+$/, '') + '/openapi/serverManFile/download?ticket='
                + encodeURIComponent(ticket);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        // ---------- 预览 ----------
        canPreview(item) {
            // 后端还会再校验一次，这里只是避免给用户一个必然失败的按钮
            return !item.dir && (!this.previewMaxBytes || item.size <= this.previewMaxBytes);
        },
        openPreview(item) {
            this.preview.visible = true;
            this.preview.loading = true;
            this.preview.name = item.name;
            this.preview.path = item.path;
            this.preview.sizeText = item.sizeText;
            this.preview.modifyTime = item.modifyTime;
            this.preview.permission = item.permission;
            this.preview.content = '';
            this.preview.rawContent = '';
            this.preview.formatted = false;

            request({
                url: '/serverManFile/preview',
                method: 'post',
                data: { machineId: this.machine.id, path: item.path }
            })
                .then(res => {
                    this.preview.name = res.meta.name;
                    this.preview.path = res.meta.path;
                    this.preview.rawContent = res.meta.content || '';
                    this.preview.content = this.preview.rawContent;
                })
                .catch(() => {
                    // 失败原因（过大 / 二进制 / 无权限）已由拦截器提示，这里直接关掉弹窗
                    this.preview.visible = false;
                })
                .finally(() => {
                    this.preview.loading = false;
                });
        },
        toggleJsonFormat() {
            if (this.preview.formatted) {
                this.preview.content = this.preview.rawContent;
                this.preview.formatted = false;
                return;
            }
            try {
                // 始终以原文为源做格式化，来回切换不会层层缩进
                const parsed = JSON.parse(this.preview.rawContent);
                this.preview.content = JSON.stringify(parsed, null, 4);
                this.preview.formatted = true;
            } catch (e) {
                ElMessage({ message: '内容不是合法的 JSON，无法格式化', type: 'warning', duration: 3 * 1000 });
            }
        },

        // ---------- 上传 ----------
        pickFile() {
            // 先清空，否则连续选同一个文件不会触发 change
            this.$refs.fileInput.value = '';
            this.$refs.fileInput.click();
        },
        onFilePicked(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) {
                return;
            }
            // 当前目录已有同名文件时先确认一次；后端也会挡，这里只是给出更友好的交互
            const conflict = this.items.some(item => item.name === file.name);
            if (conflict) {
                ElMessageBox.confirm(`当前目录已存在同名文件「${file.name}」，确定要覆盖吗？`, '覆盖确认', {
                    confirmButtonText: '覆盖',
                    cancelButtonText: '取消',
                    type: 'warning'
                })
                    .then(() => this.upload(file, true))
                    .catch(() => {});
            } else {
                this.upload(file, false);
            }
        },
        upload(file, overwrite) {
            const form = new FormData();
            form.append('machineId', this.machine.id);
            form.append('path', this.path || '');
            form.append('overwrite', overwrite ? 'true' : 'false');
            form.append('file', file);

            this.uploading = true;
            request({
                url: '/serverManFile/upload',
                method: 'post',
                data: form,
                // 大文件上传可能远超默认的 120 秒，这里显式关闭超时
                timeout: 0
            })
                .then(res => {
                    ElMessage.success('上传完成：' + res.meta.path);
                    this.refresh();
                })
                .catch(() => {})
                .finally(() => {
                    this.uploading = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
/* 面板本体：内嵌在控制台抽屉右侧，所以左边给一条分隔线、自己撑满高度 */
.fp {
    position: relative;
    display: flex;
    flex: none;
    flex-direction: column;
    /* 宽度由拖拽决定，通过 CSS 变量下发；媒体查询里的 width:100%（移动端）能正常覆盖它 */
    width: var(--fp-width, 380px);
    height: 100%;
    border-left: 1px solid #e6eaf0;
    background-color: #ffffff;
}

/* 拖拽中：禁掉选中，避免一边拖一边把页面文字刷蓝 */
.fp.is-resizing {
    user-select: none;
}

/* 拖拽手柄：压在面板左边缘上，命中区域比视觉宽度大一些，好抓 */
.fp-resizer {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -3px;
    z-index: 2;
    width: 7px;
    cursor: col-resize;
    background-color: transparent;
    transition: background-color 0.15s;
}

.fp-resizer:hover,
.fp-resizer.is-active {
    background-color: var(--el-color-primary-light-7);
}

/* ---------- 面板头 ---------- */
.fp-head {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.fp-head__title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.fp-head__count {
    flex: 1 1 auto;
    font-size: 12px;
    color: #aab4c4;
}

.fp-head__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 2px;
}

.fp-icon-btn {
    height: 24px;
    margin-left: 0;
    padding: 0 6px;
}

/* ---------- 路径栏 ---------- */
.fp-path {
    display: flex;
    flex: none;
    align-items: center;
    gap: 2px;
    padding: 6px 6px 6px 8px;
    border-bottom: 1px solid #f2f5fa;
    background-color: #fbfcfe;
}

.fp-path__btn {
    flex: none;
    height: 22px;
    margin-left: 0;
    padding: 0 5px;
    color: #8a94a6;
}

.fp-path__btn:hover {
    color: var(--el-color-primary);
    background-color: transparent;
}

/* 隐藏文件开关打开时高亮，避免"看不出当前是开还是关" */
.fp-path__btn.is-on {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

/* 面包屑：点任意一层直接跳过去；路径过长时折行 */
.fp-crumb {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    min-width: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
}

.fp-crumb__item {
    max-width: 100%;
    padding: 0 3px;
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--el-color-primary);
    cursor: pointer;
}

.fp-crumb__item:hover {
    background-color: var(--el-color-primary-light-9);
}

/* 当前所在层：不可点，用正文色，避免"点了没反应"的困惑 */
.fp-crumb__item.is-current {
    color: #1f2d3d;
    cursor: default;
}

.fp-crumb__item.is-current:hover {
    background-color: transparent;
}

.fp-crumb__sep {
    color: #c0c4cc;
    user-select: none;
}

/* ---------- 列表 ---------- */
.fp-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 4px 0;
}

.fp-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px 5px 10px;
    transition: background-color 0.15s;
}

.fp-row:hover {
    background-color: #f6f9ff;
}

.fp-row__icon {
    flex: none;
    color: #a3b1c6;
}

.fp-row__icon.is-dir {
    color: var(--el-color-primary-light-3);
}

.fp-row__main {
    flex: 1 1 auto;
    min-width: 0;
}

.fp-row__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: #4a5568;
}

.fp-row__name.is-dir {
    color: var(--el-color-primary);
    cursor: pointer;
}

.fp-row__name.is-dir:hover {
    text-decoration: underline;
}

.fp-row__meta {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 1px;
    overflow: hidden;
    white-space: nowrap;
    font-size: 11px;
    color: #aab4c4;
}

.fp-row__sep {
    color: #d8dee8;
}

.fp-row__perm {
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.fp-row__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 2px;
}

.fp-empty {
    padding: 24px 12px;
    text-align: center;
    font-size: 12px;
    color: #aab4c4;
}

.fp-input {
    display: none;
}

/* ---------- 预览弹窗 ---------- */
.ssh-file-preview__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
}

.ssh-file-preview__path {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: #8a94a6;
}

.ssh-file-preview__size {
    flex: none;
    font-size: 12px;
    color: #8a94a6;
}

.ssh-file-preview__btn {
    flex: none;
    margin-left: 0;
    border-radius: 8px;
}

/* 自动换行开关：紧贴右侧按钮，不抢路径的宽度 */
.ssh-file-preview__wrap {
    flex: none;
    margin-right: 0;
    height: 24px;
}

.ssh-file-preview__wrap :deep(.el-checkbox__label) {
    padding-left: 6px;
    font-size: 12px;
}

/* 文件属性行：修改时间 + 权限（含人话说明）；内容长时自动折行 */
.ssh-file-preview__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    padding: 6px 10px;
    border-radius: 8px;
    background-color: #f6f8fb;
    font-size: 12px;
    line-height: 18px;
    color: #5a6472;
}

.ssh-file-preview__meta-item {
    min-width: 0;
}

.ssh-file-preview__meta-sep {
    color: #c8cdd6;
    user-select: none;
}

.ssh-file-preview__perm {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #1f2d3d;
}

/* 八进制写法用暖色，方便一眼找到（要 chmod 时直接抄） */
.ssh-file-preview__octal {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #e6a23c;
}

/* 权限的人话说明：比主信息弱一点，不抢焦点 */
.ssh-file-preview__meta-item.is-desc {
    color: #8a94a6;
}

.ssh-file-preview__body {
    max-height: 64vh;
    min-height: 120px;
    overflow: auto;
    border: 1px solid #eef1f6;
    border-radius: 10px;
    background-color: #fbfcfe;
}

/* ---------- 逐行渲染（自动换行 + 保留行号） ---------- */
/* 行数在阈值内时用这套：换行出的续行属于同一逻辑行，行号只在该行顶部出现一次 */
.ssh-file-preview__rows {
    padding: 12px 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #1f2d3d;
}

.ssh-file-preview__row {
    display: flex;
    align-items: flex-start;
    /* 空行也必须占满一行高度，否则空行的行号会和下一行叠在一起 */
    min-height: 1.6em;
}

/* 悬停整行高亮：顺便让人看清"这几个视觉行属于同一个行号" */
.ssh-file-preview__row:hover {
    background-color: #f2f6fd;
}

.ssh-file-preview__row-no {
    flex: none;
    min-width: 3.5em;
    padding: 0 10px 0 14px;
    text-align: right;
    color: #a3b1c6;
    user-select: none;
}

.ssh-file-preview__row-text {
    flex: 1 1 auto;
    min-width: 0;
    padding-right: 14px;
    white-space: pre-wrap;
    /* 无空格的超长串（base64、URL）也要能断行，否则会撑出横向滚动 */
    overflow-wrap: anywhere;
    tab-size: 4;
}

.ssh-file-preview__inner {
    display: flex;
    align-items: flex-start;
    width: max-content;
    min-width: 100%;
}

/* 自动换行模式：内容按容器宽度折行，不再横向撑开 */
.ssh-file-preview__inner.is-wrap {
    width: auto;
    min-width: 0;
}

.ssh-file-preview__inner.is-wrap .ssh-file-preview__code {
    flex: 1 1 auto;
    min-width: 0;
    white-space: pre-wrap;
    /* 日志里常有超长且无空格的串（base64、URL），只靠 pre-wrap 不会断行 */
    overflow-wrap: anywhere;
}

/* 行号列：sticky 保证横向滚动时也一直可见 */
.ssh-file-preview__gutter {
    position: sticky;
    left: 0;
    z-index: 1;
    flex: none;
    margin: 0;
    padding: 12px 10px 12px 14px;
    border-right: 1px solid #eef1f6;
    background-color: #f6f8fb;
    text-align: right;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #a3b1c6;
    user-select: none;
}

/* 内容列：不换行，才能与行号严格 1:1 对齐（长行靠横向滚动查看） */
.ssh-file-preview__code {
    flex: none;
    margin: 0;
    padding: 12px 14px;
    white-space: pre;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #1f2d3d;
    tab-size: 4;
}

/* ---------- 小屏适配 ---------- */
@media (max-width: 768px) {
    /* 手机上终端和文件面板无法并排：面板铺满，收起后回到终端 */
    .fp {
        width: 100%;
        border-left: 0;
    }
    /* 窄屏上面板宽度由布局决定（铺满），拖拽没有意义 */
    .fp-resizer {
        display: none;
    }
}
</style>

<!-- 预览弹窗：append-to-body 之后拿不到 scoped 属性，Element 内部结构的样式要放在非 scoped 块里 -->
<style lang="scss">
.ssh-file-preview .el-dialog__body {
    padding: 0 16px 16px;
}
</style>
